/**
 * AttentionWidget — algorithmic outliers in the user's portfolio.
 *
 * Reads from `/api/govdata/anomalies/?portfolio={id}` (Phase M.M11) and
 * surfaces 3-8 cards summarizing the largest YoY changes on key metrics
 * the leader should pay attention to. Each card shows the metric, the
 * delta, and severity color coding.
 *
 * Phase I1 of issue #90 v3 plan. Pushes signal to the leader so they
 * don't have to know to look.
 */
import { useEffect, useState } from 'react'
import { AlertTriangle, ArrowDown, ArrowUp, Sparkles } from 'lucide-react'
import { api } from '../../services/api'
import { useExecDashboard } from '../../contexts/ExecDashboardContext'

interface Anomaly {
  metric: string
  metric_label: string
  fiscal_year: number
  prior_fiscal_year: number
  latest_value: number
  prior_value: number
  delta: number
  severity: 'high' | 'medium'
  direction: 'up' | 'down'
  explanation: string
}

interface AnomaliesResponse {
  anomalies: Anomaly[]
  fiscal_year?: number
  prior_fiscal_year?: number
  message?: string
}

const HUMAN_LABELS: Record<string, string> = {
  full_open_pct: 'Full & Open Competition Rate',
  single_offer_pct: 'Single-Bid Rate',
  sb_dollar_pct: 'Small Business $ Share',
  not_competed_pct: 'Not-Competed Rate',
  ot_award_pct: 'OT Adoption Rate',
  total_obligated: 'Total Obligated $',
}

function formatDelta(metric: string, delta: number): string {
  if (metric.endsWith('_pct')) {
    return `${delta > 0 ? '+' : ''}${(delta * 100).toFixed(1)}pp`
  }
  return `${delta > 0 ? '+' : ''}${(delta * 100).toFixed(0)}%`
}

export default function AttentionWidget() {
  const { dashboard } = useExecDashboard()
  const [data, setData] = useState<AnomaliesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const portfolioId = dashboard?.portfolio_group as string | undefined

  useEffect(() => {
    if (!portfolioId) {
      setData({ anomalies: [], message: 'Scope this dashboard to a portfolio to see attention items.' })
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    api
      .getRaw<AnomaliesResponse>('/api/govdata/anomalies/', { portfolio: portfolioId })
      .then((d) => {
        setData(d)
        setError(null)
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to load anomalies'
        setError(msg)
        setData({ anomalies: [] })
      })
      .finally(() => setLoading(false))
  }, [portfolioId])

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-3 h-full">
        <div className="flex items-center gap-2 text-gray-400">
          <Sparkles size={14} />
          <span className="text-xs font-medium">Attention</span>
        </div>
        <div className="flex items-center justify-center h-20">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
        </div>
      </div>
    )
  }

  const anomalies = data?.anomalies ?? []

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-3 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Sparkles size={14} className="text-amber-500" />
          <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">Attention</span>
          {anomalies.length > 0 && (
            <span className="text-[10px] text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full px-1.5 py-0.5">
              {anomalies.length}
            </span>
          )}
        </div>
        {data?.fiscal_year && (
          <span className="text-[10px] text-gray-400">FY{data.prior_fiscal_year}→FY{data.fiscal_year}</span>
        )}
      </div>

      {error ? (
        <div className="flex flex-col items-center justify-center py-6 text-red-500" title={error}>
          <AlertTriangle size={20} className="opacity-60 mb-1" />
          <p className="text-[11px] text-center">Failed to load anomalies</p>
        </div>
      ) : anomalies.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-6 text-gray-400">
          <AlertTriangle size={20} className="opacity-40 mb-1" />
          <p className="text-[11px] text-center">
            {data?.message ?? 'No anomalies in your portfolio'}
          </p>
        </div>
      ) : (
        <ul className="space-y-1.5">
          {anomalies.map((a, i) => {
            const severityClass = a.severity === 'high'
              ? 'border-l-red-500 bg-red-50 dark:bg-red-900/20'
              : 'border-l-amber-500 bg-amber-50 dark:bg-amber-900/20'
            const arrow = a.direction === 'up'
              ? <ArrowUp size={11} className="text-red-500" />
              : <ArrowDown size={11} className="text-red-500" />
            return (
              <li
                key={i}
                className={`border-l-2 ${severityClass} pl-2 pr-2 py-1 rounded-r`}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="text-[11px] font-medium text-gray-900 dark:text-gray-100">
                    {HUMAN_LABELS[a.metric] ?? a.metric_label}
                  </span>
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-mono text-gray-700 dark:text-gray-300">
                    {arrow}
                    {formatDelta(a.metric, a.delta)}
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
